import { PaneRoute, PaneRouter, PaneRoutes } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { RIGHT_PANEL_WIDTH_STORAGE_KEY } from "@/context/PaneLayoutWidthsContext";
import { EmailDetailPane } from "@/panes/canvas-experiments/email-detail";
import { NotionDetailPane } from "@/panes/canvas-experiments/notion-detail";
import { ConnectionPane } from "@/panes/connection/ConnectionPane";
import { EntitiesPane } from "@/panes/entities";
import { EntityDetailPane } from "@/panes/entity-detail";
import { FileRecordDetailPane } from "@/panes/file-record-detail";
import {
  GitHubIssueDetailPane,
  GitHubProjectDetailPane,
  GitHubPullRequestDetailPane,
  GitHubRepoDetailPane,
} from "@/panes/github-detail";
import { CalendarEventDetailPane } from "@/panes/google-calendar-detail";
import { GoogleDriveFileDetailPane } from "@/panes/google-drive-detail";
import { GranolaMeetingDetailPane } from "@/panes/granola-detail";
import { SessionPane } from "@/panes/session";
import { SkillTemplatePane } from "@/panes/skill-template/SkillTemplatePane";
import { SkillPane } from "@/panes/skill/SkillPane";
import { SlackMessageDetailPane } from "@/panes/slack-detail";
import { ResizablePanel } from "@cohesive/ui-components";
import { useCallback, useEffect, useRef } from "react";
import { useWindowSize } from "usehooks-ts";

export type RightPaneRouterMode = "legacy" | "workspace";

export function RightPaneRouter({
  mode,
  maxPaneWidth,
  isRightPanelOpen,
  onRightPanelWidthChange,
  setIsExpanded,
  isExpanded,
  leftChatDockPx,
  leftRailWidth = 0,
}: {
  mode: RightPaneRouterMode;
  maxPaneWidth: number;
  isRightPanelOpen: boolean;
  onRightPanelWidthChange: (width: number) => void;
  setIsExpanded: (value: boolean) => void;
  isExpanded: boolean;
  /** Left space reserved by a docked canvas chat; shrinks the expanded pane by this much. */
  leftChatDockPx: number;
  /**
   * Horizontal space the left rail column actually occupies in the layout row
   * (0 when the rail is collapsed); subtracted when expanding in workspace mode.
   */
  leftRailWidth?: number;
}) {
  // Live window width so the expanded pane tracks window resizes; a stale
  // snapshot here is what used to leave the pane too narrow or wider than
  // the viewport.
  const { width: windowWidth } = useWindowSize();

  const leftOffset =
    mode === "workspace" ? leftRailWidth + leftChatDockPx : leftChatDockPx;
  const expandedWidth = Math.max(400, windowWidth - leftOffset);

  useEffect(() => {
    if (!isRightPanelOpen) {
      setIsExpanded(false);
    }
  }, [isRightPanelOpen, setIsExpanded]);

  // Expansion is a render-time override (`renderedWidth`), so the stored width
  // never changes while expanded. Consumers still need the *effective* width:
  // remember the stored width and report whichever one is currently rendered.
  const lastStoredWidthRef = useRef(0);
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  const handleWidthChange = useCallback(
    (width: number) => {
      lastStoredWidthRef.current = width;
      if (!isExpandedRef.current) {
        onRightPanelWidthChange(width);
      }
    },
    [onRightPanelWidthChange],
  );

  useEffect(() => {
    onRightPanelWidthChange(
      isExpanded ? expandedWidth : lastStoredWidthRef.current,
    );
  }, [isExpanded, expandedWidth, onRightPanelWidthChange]);

  return (
    <ResizablePanel
      directions={isExpanded ? [] : ["left"]}
      isClosed={!isRightPanelOpen}
      minWidth={400}
      maxWidth={maxPaneWidth}
      renderedWidth={isExpanded ? expandedWidth : undefined}
      // Beside a left chat dock the expanded width follows the dock's LIVE
      // drag width — the panel's width transition would trail the dock and
      // flash the squeezed content region in the gap, so snap instead. The
      // gate flips off in the same commit `leftChatDockPx` returns to 0, so
      // un-docking and collapse-to-default keep their animation.
      transitionDuration={isExpanded && leftChatDockPx > 0 ? 0 : undefined}
      storageKey={RIGHT_PANEL_WIDTH_STORAGE_KEY}
      onWidthChange={handleWidthChange}
      showHandleIndicator
      // overflow-visible so the resize handle pill (which straddles the left
      // edge) isn't clipped over the canvas; same opt-in the left rail uses.
      // It applies only while the panel is open — ResizablePanel re-clips
      // itself whenever the content is pinned oversized (closed/animating).
      className="bg-surface-rail border-l h-full overflow-visible"
    >
      <PaneRouter pane="rightPane">
        <PaneRoutes>
          <PaneRoute path={ROUTES.SESSIONS.SESSION} element={<SessionPane />} />
          <PaneRoute path={ROUTES.INBOX.ITEM} element={<SessionPane />} />
          <PaneRoute
            path={ROUTES.FILE_RECORDS.DETAIL}
            element={<FileRecordDetailPane />}
          />
          <PaneRoute path={ROUTES.ENTITIES.LIST} element={<EntitiesPane />} />
          <PaneRoute
            path={ROUTES.ENTITIES.DETAIL}
            element={<EntityDetailPane />}
          />
          <PaneRoute
            path={ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL}
            element={<NotionDetailPane />}
          />
          <PaneRoute
            path={ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL}
            element={<EmailDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GOOGLE_DRIVE.FILE_DETAIL}
            element={<GoogleDriveFileDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.ISSUE_DETAIL}
            element={<GitHubIssueDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.PR_DETAIL}
            element={<GitHubPullRequestDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.REPO_DETAIL}
            element={<GitHubRepoDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.PROJECT_DETAIL}
            element={<GitHubProjectDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GOOGLE_CALENDAR.EVENT_DETAIL}
            element={<CalendarEventDetailPane />}
          />
          <PaneRoute
            path={ROUTES.SLACK.MESSAGE_DETAIL}
            element={<SlackMessageDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GRANOLA.MEETING_DETAIL}
            element={<GranolaMeetingDetailPane />}
          />
          <PaneRoute path={ROUTES.SKILLS.SHOW} element={<SkillPane />} />
          <PaneRoute
            path={ROUTES.SKILL_TEMPLATES.SHOW}
            element={<SkillTemplatePane />}
          />
          <PaneRoute
            path={ROUTES.SETTINGS.CONNECTORS_SETTINGS}
            element={<ConnectionPane />}
          />
        </PaneRoutes>
      </PaneRouter>
    </ResizablePanel>
  );
}
